import React, { useState } from "react";
import { Search, TrendingUp, ArrowUpCircle, Users, Globe2 } from "lucide-react";
import { api } from "@/lib/api";

function authHeaders() {
  const t = localStorage.getItem("fliboe_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const PLAN_OPTIONS = ["free", "starter", "pro", "agency", "unlimited", "earn"];

/**
 * Admin-only "user support" console.
 * Search a user by name / email → see their stations + pulse sites → upgrade a
 * pulse plan or pull station geo/demographics analytics inline.
 */
export default function OwnerToolsCard() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [analytics, setAnalytics] = useState({}); // { [station_id]: data }
  const [err, setErr] = useState("");

  const search = async () => {
    if (!q.trim()) return;
    setBusy(true); setErr(""); setResults([]);
    try {
      const { data } = await api.get(`/admin/users-search`, { params: { q }, headers: authHeaders() });
      setResults(data || []);
      if (!data || !data.length) setErr(`No user matched "${q}"`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Search failed");
    } finally { setBusy(false); }
  };

  const setPlan = async (site_id, plan, siteName) => {
    if (!window.confirm(`Change ${siteName} to plan "${plan}"?`)) return;
    try {
      await api.post(`/admin/pulse/sites/${site_id}/set-plan`, { plan }, { headers: authHeaders() });
      alert(`✓ Upgraded to ${plan}.`);
      search();
    } catch (e) {
      alert(e?.response?.data?.detail || "Update failed");
    }
  };

  const loadAnalytics = async (station_id) => {
    try {
      const { data } = await api.get(`/admin/stations/${station_id}/analytics`, { headers: authHeaders() });
      setAnalytics(prev => ({ ...prev, [station_id]: data }));
    } catch (e) {
      alert(e?.response?.data?.detail || "Analytics failed");
    }
  };

  return (
    <section className="fl-card p-6 mt-6" data-testid="owner-tools-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center">
          <Users className="w-5 h-5 text-emerald-400" />
        </div>
        <div>
          <h2 className="fl-display text-xl font-bold text-white">User support console</h2>
          <p className="text-white/50 text-sm mt-0.5">Search any user → upgrade their Pulse plan or pull their station&apos;s listener geo/age/gender.</p>
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        <input
          value={q} onChange={e => setQ(e.target.value)}
          onKeyDown={e => e.key === "Enter" && search()}
          className="fl-input flex-1"
          placeholder="Search by name or email (e.g. Deborah)"
          data-testid="owner-tools-search-input"
        />
        <button onClick={search} disabled={busy} className="fl-btn" data-testid="owner-tools-search-btn">
          <Search className="w-4 h-4" /> {busy ? "Searching…" : "Search"}
        </button>
      </div>

      {err && <div className="text-white/50 text-sm mb-3">{err}</div>}

      <div className="space-y-4" data-testid="owner-tools-results">
        {results.map(u => (
          <div key={u.user_id} className="fl-card p-4" data-testid={`owner-tools-user-${u.user_id}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center text-emerald-300 font-bold">
                {(u.name || u.email || "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-white font-bold truncate">{u.name || u.email}</div>
                <div className="text-white/50 text-xs truncate">{u.email} · plan={u.plan || "free"}</div>
              </div>
            </div>

            {u.pulse_sites?.length > 0 && (
              <div className="mt-3">
                <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold mb-1">Pulse sites</div>
                {u.pulse_sites.map(ps => (
                  <div key={ps.site_id} className="flex items-center gap-2 py-1.5 flex-wrap" data-testid={`owner-tools-site-${ps.site_id}`}>
                    <div className="text-white text-sm flex-1 min-w-0 truncate">
                      <span className="font-semibold">{ps.name}</span>
                      <span className="text-white/40 text-xs ml-2">plan: {ps.plan || "free"}</span>
                    </div>
                    <select
                      className="fl-input !py-1 !px-2 !text-xs"
                      defaultValue={ps.plan || "free"}
                      onChange={e => setPlan(ps.site_id, e.target.value, ps.name)}
                      data-testid={`owner-tools-plan-${ps.site_id}`}
                    >
                      {PLAN_OPTIONS.map(p => (<option key={p} value={p}>{p}</option>))}
                    </select>
                  </div>
                ))}
              </div>
            )}

            {u.stations?.length > 0 && (
              <div className="mt-3">
                <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold mb-1">Stations</div>
                {u.stations.map(st => (
                  <div key={st.station_id} className="py-1.5" data-testid={`owner-tools-station-${st.station_id}`}>
                    <div className="flex items-center gap-2">
                      <div className="text-white text-sm flex-1 min-w-0 truncate">
                        <span className="font-semibold">{st.name}</span>
                        <span className="text-white/40 text-xs ml-2">{st.listener_count || 0} listeners</span>
                      </div>
                      <button
                        onClick={() => loadAnalytics(st.station_id)}
                        className="fl-btn ghost !py-1 !px-2 !text-xs"
                        data-testid={`owner-tools-analytics-${st.station_id}`}
                      >
                        <TrendingUp className="w-3.5 h-3.5" /> Analytics
                      </button>
                    </div>
                    {analytics[st.station_id] && (
                      <div className="mt-2 fl-card p-3 bg-black/40" data-testid={`owner-tools-analytics-panel-${st.station_id}`}>
                        <div className="text-white/70 text-xs mb-2">
                          <strong className="text-white">{analytics[st.station_id].current_listeners}</strong> live listeners · demographics known for <strong className="text-white">{analytics[st.station_id].known_demographics_pct}%</strong>
                        </div>
                        <StatRow icon={<Globe2 className="w-3.5 h-3.5 text-emerald-300" />} label="By country" data={analytics[st.station_id].listeners_by_country} />
                        <StatRow label="By age" data={analytics[st.station_id].listeners_by_age} />
                        <StatRow label="By gender" data={analytics[st.station_id].listeners_by_gender} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function StatRow({ icon, label, data }) {
  const entries = Object.entries(data || {}).sort(([, a], [, b]) => b - a);
  const total = entries.reduce((s, [, n]) => s + n, 0);
  if (!entries.length || total === 0) return null;
  return (
    <div className="mt-1">
      <div className="text-[10px] uppercase tracking-widest text-white/40 flex items-center gap-1 mb-1">{icon}{label}</div>
      <div className="flex flex-wrap gap-1">
        {entries.map(([k, n]) => (
          <span key={k} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-xs text-white">
            <span className="text-white/60">{k}</span>
            <span className="font-mono text-emerald-300">{n}</span>
            <span className="text-white/30 text-[10px]">{Math.round((n / total) * 100)}%</span>
          </span>
        ))}
      </div>
    </div>
  );
}
